import { RiskRating } from "~/graphql/supergraphEnums";
import {
  type PaymentAccountInput,
  type PaymentAccountOverridesType,
  type ProfileType,
  type RiskRating as RiskRatingType,
} from "~/graphql/supergraphTypes";
import { Profile } from "../../../PaymentInstructionChange/PositionsTable/types";
import { ComplianceCaseProfileType } from "../../types";
import { getProfileName } from "../../utils";
import { INITIAL_VALUES } from "../CreateBankAccountAction/constants";
import { FormValues, Option } from "../CreateBankAccountAction/types";
import { CompliancePaymentAccountType } from "../EditBankAccountAction/types";

type Mode = "create" | "edit";

const DEFAULT_PROFILE_ID = -1;
const DEFAULT_EMPTY_STRING = "";

const getAccountHolderType = (
  compliancePaymentAccount?: CompliancePaymentAccountType,
): "" | "individual" | "institution" =>
  (compliancePaymentAccount?.profile?.complianceObjectType ?? DEFAULT_EMPTY_STRING) as
    "" | "individual" | "institution";

const getAccountNameMatches = (mismatchReason: string | null): "yes" | "no" =>
  mismatchReason === null ? "yes" : "no";

const createBaseFormValues = (
  showBankAndCountry: boolean,
  compliancePaymentAccount?: CompliancePaymentAccountType,
): FormValues => ({
  accountHolderId: compliancePaymentAccount?.profileId ?? DEFAULT_PROFILE_ID,
  bankName: compliancePaymentAccount?.providerName ?? DEFAULT_EMPTY_STRING,
  country: compliancePaymentAccount?.bankCountry?.alpha2code ?? DEFAULT_EMPTY_STRING,
  accountNameMatches: compliancePaymentAccount
    ? getAccountNameMatches(compliancePaymentAccount.mismatchReason)
    : null,
  bankAccountName: compliancePaymentAccount?.payeeName ?? DEFAULT_EMPTY_STRING,
  explanation: compliancePaymentAccount?.mismatchReason ?? DEFAULT_EMPTY_STRING,
  accountHolderType: getAccountHolderType(compliancePaymentAccount),
  accountHolderFirstName: compliancePaymentAccount?.profile?.firstName ?? DEFAULT_EMPTY_STRING,
  accountHolderMiddleName:
    compliancePaymentAccount?.profile?.middleName ?? DEFAULT_EMPTY_STRING,
  accountHolderLastName: compliancePaymentAccount?.profile?.lastName ?? DEFAULT_EMPTY_STRING,
  accountHolderLegalName: compliancePaymentAccount?.profile?.legalName ?? DEFAULT_EMPTY_STRING,
  riskRating: compliancePaymentAccount?.riskRating?.overriddenRating ?? RiskRating.None,
  showBankAndCountry,
});

export const prepareInitialValues = (
  mode: Mode,
  compliancePaymentAccount?: CompliancePaymentAccountType,
  showBankAndCountry = true,
  isPICR?: boolean,
): FormValues => {
  if (mode === "edit" && compliancePaymentAccount) {
    return createBaseFormValues(isPICR ? false : showBankAndCountry, compliancePaymentAccount);
  }

  return {
    ...INITIAL_VALUES,
    showBankAndCountry: isPICR ? false : showBankAndCountry,
  };
};

export const prepareCreateRequestPayload = (
  values: Partial<FormValues>,
): PaymentAccountInput => ({
  profileId: values.accountHolderId ?? DEFAULT_PROFILE_ID,
  providerName: values.bankName ?? DEFAULT_EMPTY_STRING,
  bankCountry: values.country ?? DEFAULT_EMPTY_STRING,
  payeeName: values.accountNameMatches === "no" ? values.bankAccountName : null,
  mismatchReason: values.accountNameMatches === "no" ? values.explanation : null,
  overriddenRiskRating: values.riskRating == RiskRating.None ? null : values.riskRating,
});

export const prepareCreateRequestPayloadForPICR = (values: Partial<FormValues>) => ({
  linkedProfileId: values.accountHolderId ?? DEFAULT_PROFILE_ID,
  paymentAccountOverrides: {
    payeeName: values.accountNameMatches === "no" ? (values.bankAccountName ?? "") : null,
    mismatchReason: values.accountNameMatches === "no" ? (values.explanation ?? "") : null,
    overriddenRiskRating: values.riskRating == RiskRating.None ? null : values.riskRating,
  } as PaymentAccountOverridesType,
});

const getOverriddenRiskRating = (
  oldRiskRating?: RiskRatingType | null,
  newRiskRating?: RiskRatingType,
) => {
  if (newRiskRating == RiskRating.None) {
    if (oldRiskRating && oldRiskRating != RiskRating.None) {
      return newRiskRating;
    }
  } else {
    return newRiskRating;
  }
};

export const prepareEditRequestPayloadForPICR = (
  values: Partial<FormValues>,
  initialValues: FormValues,
  compliancePaymentAccount?: CompliancePaymentAccountType,
) => ({
  linkedProfileId:
    values.accountHolderId ?? compliancePaymentAccount?.profileId ?? DEFAULT_PROFILE_ID,
  paymentAccountOverrides: {
    payeeName:
      (values.accountNameMatches ?? initialValues.accountNameMatches) === "no"
        ? (values.bankAccountName ?? initialValues.bankAccountName)
        : null,
    mismatchReason:
      (values.accountNameMatches ?? initialValues.accountNameMatches) === "no"
        ? (values.explanation ?? initialValues.explanation)
        : null,
    overriddenRiskRating: getOverriddenRiskRating(
      compliancePaymentAccount?.riskRating?.overriddenRating,
      values.riskRating,
    ),
  } as PaymentAccountOverridesType,
});

export const prepareEditRequestPayload = (
  values: Partial<FormValues>,
  initialValues: FormValues,
  compliancePaymentAccount?: CompliancePaymentAccountType,
): PaymentAccountInput => ({
  profileId:
    values.accountHolderId ?? compliancePaymentAccount?.profileId ?? DEFAULT_PROFILE_ID,
  providerName: values.bankName ?? initialValues.bankName,
  bankCountry: values.country ?? initialValues.country,
  payeeName:
    (values.accountNameMatches ?? initialValues.accountNameMatches) === "no"
      ? (values.bankAccountName ?? initialValues.bankAccountName)
      : null,
  mismatchReason:
    (values.accountNameMatches ?? initialValues.accountNameMatches) === "no"
      ? (values.explanation ?? initialValues.explanation)
      : null,
  overriddenRiskRating: getOverriddenRiskRating(
    compliancePaymentAccount?.riskRating?.overriddenRating,
    values.riskRating,
  ),
});

export const prepareProfileOptions = (
  profiles?: ComplianceCaseProfileType[] | Profile[],
  showModal?: boolean,
): Option[] => {
  if (!profiles) return [];

  return profiles
    .map((profile) => {
      if (!profile) return null;

      if (showModal) {
        return {
          id: profile.id,
          name:
            profile.legalName !== null && profile.legalName !== ""
              ? profile.legalName
              : `${profile.firstName} ${profile.lastName}`,
        } as Option;
      }

      if ("parentRelationships" in profile) {
        const hasValidRelationship = profile.parentRelationships?.some(
          (relationship: { relationshipType: string }) =>
            ["owner", "controller", "payment_beneficiary", "unassigned"].includes(
              relationship.relationshipType.toLowerCase(),
            ),
        );

        if (!hasValidRelationship) return null;
      }

      if (!profile.id || profile.id <= 0) return null;

      return {
        id: profile.id,
        name: getProfileName(profile as unknown as ProfileType),
      };
    })
    .filter((option): option is Option => option !== null);
};
